<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { Menu } from "floating-vue";
import { type Component, computed, inject, nextTick, provide, ref, useSlots, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { type RouteLocationRaw, RouterLink } from "vue-router";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";

import DropdownMenuItem from "~/components/dumb/DropdownMenuItem.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import GlobalPopper, { PopperType } from "~/components/globalPopper";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import { MAX_ITEMS_PER_SECTION, ROW_TYPE } from "~/constants/dropdown";
import {
  DROPDOWN_NESTING_LEVEL,
  DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION,
  DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION,
  DROPDOWN_REGISTER_SUBDROPDOWN_STATE,
  DROPDOWN_ROOT_REFERENCE_NODE_GETTER,
} from "~/constants/injection";
import { colorsByTheme } from "~/constants/style";
import { AlertIcon, OpenExternalLinkIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import { DropdownMenuItemKind, Placement } from "~/shared/enums";
import type { DropdownMenuSection, ProcessedMenuItem } from "~/shared/types";
import { usePageStore } from "~/stores";
import { pickComponentAttrs } from "~/utils/common";
import useLastInputType from "~/utils/useLastInputType";

const openMobileSheets = new Set<() => void>();

defineOptions({ inheritAttrs: false });

type ProcessedComponent = {
  title: string;
  items: ProcessedMenuItem[];
  showTitle?: boolean;
};

const props = defineProps<{
  container?: string;
  disabled?: boolean;
  sections: DropdownMenuSection[];
  placement?: Placement;
  block?: boolean;
  heightBlock?: boolean;
  distance?: number;
  skidding?: number;
  cover?: boolean;
  widthPixels?: number;
  maxHeightPixels?: number;
  dontPropagateClose?: boolean;
  isContrast?: boolean;
  preventCloseOnSelect?: boolean;
  headerTitle?: string;
  showSearch?: boolean;
  hasInput?: boolean; // TODO this is a hack, remove it and solve a different way
  popperClass?: string;
  loading?: boolean;
  highlightOnly?: boolean;
  mobileIncludeBack?: boolean;
  mobileIncludeDone?: boolean;
  enableShowMore?: boolean;
  skipNestingLevel?: boolean;
  disableMobileSheet?: boolean;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  referenceNode?: Component | Element;
  onClick?: (event: Event) => void;
  onClickCapture?: (event: Event) => void;
  onKeydown?: (event: KeyboardEvent) => void;
  onContextmenu?: (event: MouseEvent) => void;
}>();

const pageStore = usePageStore();
const slots = useSlots();

const menu = ref<{ $el: HTMLDivElement } | null>(null);
const scrollContainer = computed<HTMLDivElement | null>(() => menu.value && menu.value.$el);
const colors = computed(() => colorsByTheme[pageStore.theme]);
const hasFooterSlot = computed(() => !!slots.footer);

const showMoreRefs = new Map();
const focusedShowMore = ref<string | null>(null);

const wrapOnClick = (fn?: (event?: Event) => void) => (event: Event) => {
  if (props.preventCloseOnSelect) {
    event.stopImmediatePropagation();
  }
  fn?.(event);
};

let currentItemIndex: number;
const indexOfFocusedItem = ref(props.highlightOnly ? 0 : -1);
const { isKeyboard: isNavigatingWithKeyboard } = useLastInputType();
const processedSections = computed(() => {
  currentItemIndex = -1;
  return props.sections
    .map((sect) => {
      const unhiddenItems = sect.items.filter((e) => !e.hidden);
      if (unhiddenItems.length === 0) {
        return null;
      }
      const res: ProcessedComponent = {
        title: sect.title,
        showTitle: sect.showTitle,
        items: unhiddenItems.map((item) => {
          const { title, icon, subtitle, selected, disabled, commandId, iconArgs, noFocus, tooltipText } = item;
          let component;
          let args;
          switch (item.kind) {
            case DropdownMenuItemKind.INTERNAL_LINK: {
              component = RouterLink;
              args = {
                onClick: wrapOnClick(item.onClick),
                to: item.navigate?.to as RouteLocationRaw,
                target: item.navigate?.newTab ? "_blank" : undefined,
              };
              break;
            }
            case DropdownMenuItemKind.EXTERNAL_LINK: {
              component = "a";
              args = {
                onClick: wrapOnClick(item.onClick),
                href: item.navigate?.to as string,
                target: item.navigate?.newTab ? "_blank" : undefined,
              };
              break;
            }
            case DropdownMenuItemKind.BUTTON: {
              component = "div";
              args = { onClick: wrapOnClick(item.onClick) };
              break;
            }
            case DropdownMenuItemKind.COMPONENT: {
              component = item.component as Component;
              args = {
                ...(item.componentArgs ?? {}),
                ...pickComponentAttrs(component, { disabled: disabled ?? false }),
              };
              break;
            }
            default: {
              throw new Error(`Unknown dropdown menu item kind: ${item.kind}`);
            }
          }
          const calcDisabled = disabled ?? false;
          if (!calcDisabled) {
            currentItemIndex += 1;
          }
          const itemIndex = calcDisabled ? -1 : currentItemIndex;
          const externalLinkIcon = item.kind === DropdownMenuItemKind.EXTERNAL_LINK && !calcDisabled;
          // Add itemIndex to args for COMPONENT items so they can register with parent
          if (item.kind === DropdownMenuItemKind.COMPONENT) {
            args = { ...args, ...pickComponentAttrs(component as Component, { itemIndex }) };
          }
          return {
            index: itemIndex,
            component,
            args,
            title,
            disabled: calcDisabled,
            disabledReason: item.disabledReason,
            selected: selected ?? false,
            noFocus,
            icon,
            iconArgs,
            iconAfter: externalLinkIcon ? OpenExternalLinkIcon : item.iconAfter,
            iconAfterArgs: externalLinkIcon ? { class: "text-lt icon-sm" } : item.iconAfterArgs,
            showIconAfterOnHover: externalLinkIcon || item.showIconAfterOnHover,
            subtitle,
            tooltipText,
            strikethrough: item.strikethrough,
            commandId,
            dataTestid: item.dataTestid,
            onAssignRef: item.onAssignRef,
          };
        }),
      };
      return res;
    })
    .filter(isDefined);
});

const dropdownEmpty = computed(() => processedSections.value.length === 0 && !props.loading);

const expandedSections = ref<Set<string>>(new Set());
const mobileSheet = ref<ComponentExposed<typeof MobileBottomSheet<boolean>> | null>(null);

const filteredSections = computed(() => {
  const searchQuery = mobileSheet.value?.search?.trim()?.toLowerCase();
  if (!searchQuery) {
    return processedSections.value;
  }
  return processedSections.value
    .map((section) => ({
      ...section,
      items: section.items.filter(({ title, subtitle }) =>
        [title, subtitle].some((text) => text?.toLowerCase().includes(searchQuery))
      ),
    }))
    .filter((section) => section.items.length);
});

const hasSingleFilteredSection = computed(() => filteredSections.value.length === 1);

const rows = computed(() => {
  const res: {
    id: string;
    type: string;
    section: ProcessedComponent;
    item?: ProcessedComponent["items"][number];
  }[] = [];
  filteredSections.value.forEach((section, index) => {
    const isExpanded = expandedSections.value.has(section.title);
    const hasMoreItems = section.items.length > MAX_ITEMS_PER_SECTION + 1;
    const shouldLimitItems = props.enableShowMore && !hasSingleFilteredSection.value;
    const visibleItems =
      shouldLimitItems && hasMoreItems && !isExpanded ? section.items.slice(0, MAX_ITEMS_PER_SECTION) : section.items;

    res.push({ id: `section-${index}-start`, type: ROW_TYPE.SECTION_START, section });
    visibleItems.forEach((item, itemIndex) => {
      res.push({ id: `section-${index}-${itemIndex}`, type: ROW_TYPE.ITEM, section, item });
    });
    if (shouldLimitItems && section.showTitle && hasMoreItems && !isExpanded) {
      res.push({ id: `section-${index}-show-more`, type: ROW_TYPE.SHOW_MORE, section });
    }
    res.push({ id: `section-${index}-end`, type: ROW_TYPE.SECTION_END, section });
  });
  return res;
});

const placementDefinite = computed(() => props.placement ?? Placement.BOTTOM_LEFT);
const defaultDistance = computed(() => props.distance ?? (props.cover ? 0 : -8));
const calcDistance = ref(defaultDistance.value);
const calcWidthPx = computed(() => props.widthPixels ?? 224);
const maxHeightPixelsNorm = computed(() => props.maxHeightPixels ?? 384);

const itemRefs = new Map();

const assignItemRef = (
  index: number,
  elem: { $el: HTMLDivElement } | null,
  item: { onAssignRef?: (e: HTMLElement) => void }
) => {
  if (!elem || !elem.$el) {
    return;
  }

  const itemExistsAtIndex = processedSections.value.some((section) =>
    section.items.some((sectionItem) => sectionItem === item && sectionItem.index === index)
  );
  if (!itemExistsAtIndex) {
    return;
  }

  itemRefs.set(index, elem.$el);
  item.onAssignRef?.(elem.$el);
};

const dropdownRef = ref<InstanceType<typeof GlobalPopper> | null>(null);
const wrapperRef = computed(() => dropdownRef.value?.triggerRef);
const recalculateDistance = () => {
  calcDistance.value = defaultDistance.value;
  if (!wrapperRef.value || !props.cover) {
    return;
  }
  calcDistance.value -= wrapperRef.value.getBoundingClientRect().height;
};
useResizeObserver(wrapperRef, recalculateDistance);
watch(() => props.cover, recalculateDistance);
watch(defaultDistance, recalculateDistance);
watch(wrapperRef, recalculateDistance, { once: true });

const focusDiv = ref<HTMLDivElement | null>(null);

const updateFocus = () => {
  if (indexOfFocusedItem.value === -1) {
    if (props.highlightOnly) {
      focusDiv.value?.scrollIntoView({ block: "nearest" });
    } else {
      focusDiv.value?.focus();
    }
    return;
  }
  if (props.highlightOnly) {
    itemRefs.get(indexOfFocusedItem.value)?.scrollIntoView({ block: "nearest" });
  } else {
    itemRefs.get(indexOfFocusedItem.value)?.focus();
  }
};

const toggleSection = (title: string) => {
  if (expandedSections.value.has(title)) {
    expandedSections.value.delete(title);
  } else {
    expandedSections.value.add(title);
    nextTick(() => {
      const section = processedSections.value.find((e) => e.title === title);
      if (section && section.items.length > MAX_ITEMS_PER_SECTION) {
        const firstNewItem = section.items[MAX_ITEMS_PER_SECTION];
        if (firstNewItem && !firstNewItem.disabled) {
          indexOfFocusedItem.value = firstNewItem.index;
          focusedShowMore.value = null;
          updateFocus();
        }
      }
    });
  }
};

const focusShowMore = (sectionTitle: string) => {
  if (isNavigatingWithKeyboard.value) {
    return;
  }
  indexOfFocusedItem.value = -1;
  focusedShowMore.value = sectionTitle;
};

const focusItem = (itemIndex: number) => {
  if (isNavigatingWithKeyboard.value) {
    return;
  }
  indexOfFocusedItem.value = itemIndex;
  focusedShowMore.value = null;
};

const clearFocus = () => {
  if (isNavigatingWithKeyboard.value) {
    return;
  }
  indexOfFocusedItem.value = -1;
  focusedShowMore.value = null;
};

const moveUpOrDown = (isUp: boolean): boolean => {
  const items = rows.value.filter(
    (e) => (e.type === ROW_TYPE.ITEM && !e.item?.disabled) || e.type === ROW_TYPE.SHOW_MORE
  );
  if (!items.length) {
    return false;
  }
  const current = items.findIndex(
    (e) =>
      (e.type === ROW_TYPE.ITEM && e.item?.index === indexOfFocusedItem.value) ||
      (e.type === ROW_TYPE.SHOW_MORE && e.section.title === focusedShowMore.value)
  );

  const next = Math.max(0, Math.min(items.length - 1, current + (isUp ? -1 : 1)));
  if (next === current && current !== -1) {
    return false;
  }
  const row = items[next];
  indexOfFocusedItem.value = row.type === ROW_TYPE.ITEM && row.item ? row.item.index : -1;
  focusedShowMore.value = row.type === ROW_TYPE.SHOW_MORE ? row.section.title : null;
  nextTick(() => {
    if (focusedShowMore.value) {
      const el = showMoreRefs.get(focusedShowMore.value);
      if (el) {
        if (props.highlightOnly) {
          el.scrollIntoView({ block: "nearest" });
        } else {
          el.focus();
        }
      }
    } else {
      updateFocus();
    }
  });

  return true;
};

const isOpen = ref(false);
const isOpening = ref(false);
const isOpenOrOpening = computed(() => isOpen.value || isOpening.value);

const openSubdropdownIndex = ref<number | null>(null);

const registerSubdropdownState = (itemIndex: number, isSubdropdownOpen: boolean) => {
  openSubdropdownIndex.value = isSubdropdownOpen ? itemIndex : null;
};

const isMobile = computed(() => pageStore.isMobile);
const showMobileSheet = computed(() => isMobile.value && !props.disableMobileSheet);

const onKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) {
    return;
  }
  const { key } = event;
  switch (key) {
    case "Enter":
    case "Shift":
    case "Meta":
    case "Control":
    case "Option":
    case "Alt": {
      break;
    }
    case "ArrowUp":
    case "ArrowDown": {
      const success = moveUpOrDown(key === "ArrowUp");
      if (success) {
        event.preventDefault();
      }
      break;
    }
    case "Tab": {
      event.preventDefault();
      moveUpOrDown(event.shiftKey);
      break;
    }
    default: {
      if (!props.hasInput) {
        dropdownRef.value?.hide();
      }
      break;
    }
  }
};

const onKeydownItem = (event: KeyboardEvent) => {
  if (!event.target || !(event.target instanceof HTMLElement)) {
    return;
  }
  const child = event.target.querySelector("div");
  if (!child) {
    return;
  }
  child.click();
};

const isHidden = ref(false);
const parentOnAfterOpen = inject(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION);
const parentOnAfterClose = inject(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION);

const onShow = () => {
  isOpen.value = true;
  isOpening.value = false;
  indexOfFocusedItem.value = props.highlightOnly ? 0 : -1;
  expandedSections.value.clear();
  focusedShowMore.value = null;
  props.onAfterOpen?.();
  parentOnAfterOpen?.();
  nextTick(() => {
    updateFocus();
  });
};

const onHide = () => {
  isOpen.value = false;
  isOpening.value = false;
  indexOfFocusedItem.value = -1;
  expandedSections.value.clear();
  focusedShowMore.value = null;
  props.onAfterClose?.();
  parentOnAfterClose?.();
};

const parentNestingLevel = inject(DROPDOWN_NESTING_LEVEL, 0);
const currentNestingLevel = parentNestingLevel + 1;
provide(DROPDOWN_NESTING_LEVEL, currentNestingLevel);

const rootReferenceNodeGetter = inject(DROPDOWN_ROOT_REFERENCE_NODE_GETTER, null);
const computedReferenceNode = computed(() => {
  if (props.skipNestingLevel) {
    return undefined;
  }
  if (currentNestingLevel > 2) {
    if (rootReferenceNodeGetter) {
      return rootReferenceNodeGetter();
    }
  }
  return props.referenceNode;
});

if (currentNestingLevel === 2) {
  provide(DROPDOWN_ROOT_REFERENCE_NODE_GETTER, () => {
    if (props.referenceNode) {
      return props.referenceNode;
    }
    return wrapperRef.value ?? null;
  });
}

provide(DROPDOWN_REGISTER_SUBDROPDOWN_STATE, registerSubdropdownState);

provide(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  if (!showMobileSheet.value && currentNestingLevel === 1) {
    return;
  }
  isHidden.value = true;
});
provide(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  if (showMobileSheet.value) {
    isHidden.value = false;
  }
  if (currentNestingLevel === 1) {
    return;
  }
  // Wait for all the menus to close before showing it again
  // oxlint-disable-next-line dart/no-set-timeout
  setTimeout(() => {
    isHidden.value = false;
  }, 300);
});

const close = () => {
  if (showMobileSheet.value) {
    isOpen.value = false;
    indexOfFocusedItem.value = -1;
    expandedSections.value.clear();
    focusedShowMore.value = null;
    props.onAfterClose?.();
    parentOnAfterClose?.();
  } else {
    dropdownRef.value?.hide();
  }
};

const open = () => {
  if (showMobileSheet.value && !props.disabled) {
    openMobileSheets.forEach((closeFn) => closeFn());
    openMobileSheets.add(close);
    isOpen.value = true;
    isHidden.value = false;
    expandedSections.value.clear();
    props.onAfterOpen?.();
    parentOnAfterOpen?.();
  } else {
    dropdownRef.value?.show();
  }
};

const onMenuItemClick = (item?: ProcessedMenuItem) => {
  const isCustom = item?.component && typeof item.component === "object" && (item.component as unknown) !== RouterLink;
  if (props.preventCloseOnSelect || isCustom) {
    return;
  }
  close();
};

const clickHighlightedItem = () => {
  if (focusedShowMore.value) {
    toggleSection(focusedShowMore.value);
    return;
  }

  const item = itemRefs.get(indexOfFocusedItem.value);
  if (!item) {
    return;
  }
  const child = item.querySelector("div");
  if (!child) {
    return;
  }
  child.click();
};

const setIndexOfFocusedItem = (index: number) => {
  indexOfFocusedItem.value = index;
};

defineExpose({
  open,
  close,
  isOpen,
  isOpenOrOpening,
  moveUpOrDown,
  clickHighlightedItem,
  setIndexOfFocusedItem,
  scrollContainer,
});
</script>

<template>
  <GlobalPopper
    v-if="!dropdownEmpty && !showMobileSheet"
    ref="dropdownRef"
    v-bind="$attrs"
    :type="PopperType.DROPDOWN"
    :container="container"
    :reference-node="() => computedReferenceNode"
    :disabled="disabled"
    :placement="placementDefinite"
    :distance="calcDistance"
    :skidding="skidding"
    :dont-propagate-close="dontPropagateClose"
    no-auto-focus
    :theme="`dropdown-${pageStore.theme}`"
    class="flex"
    :class="{
      'max-w-full grow': block,
      'h-full': heightBlock,
      'cursor-pointer': !disabled,
    }"
    :popper-class="popperClass"
    @click="onClick"
    @click.capture="onClickCapture"
    @keydown="onKeydown"
    @contextmenu="onContextmenu"
    @show="isOpening = true"
    @hide="isOpening = false"
    @apply-show="onShow"
    @apply-hide="onHide">
    <template v-if="!referenceNode" #default>
      <slot />
    </template>
    <template #popper>
      <Menu no-auto-focus>
        <DynamicScroller
          ref="menu"
          :items="rows"
          :min-item-size="4"
          key-field="id"
          class="overflow-y-auto border border-md focus-ring-none app-drag-none"
          :class="[
            block && 'w-full min-w-[224px]',
            isContrast ? 'bg-std' : 'bg-lt',
            isHidden && 'hidden',
            hasFooterSlot ? 'rounded-t rounded-b-none' : 'rounded',
          ]"
          :style="{
            'max-height': `calc(min(${maxHeightPixelsNorm}px,100vh))`,
            width: `${calcWidthPx}px`,
            'max-width': `${calcWidthPx}px`,
            '--background': isContrast ? colors.bgStd : colors.bgLt,
            '--highlight': isContrast ? colors.borderLt : colors.borderMd,
          }"
          @click.stop="updateFocus"
          @keydown.stop="onKeydown"
          @mouseleave="clearFocus"
          @focusout="clearFocus">
          <template v-if="!hasInput" #before>
            <div ref="focusDiv" tabindex="0" class="focus:outline-none" />
          </template>
          <template #default="{ item: { id, section, item, type }, index, active }">
            <!-- Section start and end -->
            <DynamicScrollerItem
              v-if="type === ROW_TYPE.SECTION_START || type === ROW_TYPE.SECTION_END"
              :item="{ id }"
              :active="active"
              class="w-full truncate border-md text-vlt"
              :class="{
                'border-b pb-1': type === ROW_TYPE.SECTION_END && index < rows.length - 1,
                'pt-1': type === ROW_TYPE.SECTION_START,
              }">
              <span
                v-if="section.showTitle && type === ROW_TYPE.SECTION_START"
                class="text-xs/3 font-semibold uppercase select-none"
                :class="isMobile ? 'px-5' : 'px-3'">
                {{ section.title }}
              </span>
            </DynamicScrollerItem>
            <!-- Show more -->
            <DynamicScrollerItem
              v-else-if="type === ROW_TYPE.SHOW_MORE && enableShowMore && section.showTitle"
              :item="{ id }"
              :active="active"
              :tabindex="0"
              class="w-full focus-ring-none"
              :class="[!isMobile && focusedShowMore === section.title && 'bg-md']"
              @keydown.enter.stop="toggleSection(section.title)"
              @mouseover="focusShowMore(section.title)"
              @focusin="focusShowMore(section.title)">
              <button
                type="button"
                class="flex w-full items-center justify-center py-1.5 text-xs font-medium text-vlt"
                tabindex="-1"
                @click.prevent.stop="toggleSection(section.title)"
                @mousedown.prevent>
                Show more
              </button>
            </DynamicScrollerItem>
            <!-- Item -->
            <DynamicScrollerItem
              v-else-if="item"
              :ref="(elem: never) => assignItemRef(item.index, elem, item)"
              :item="{ id }"
              :active="active"
              :tabindex="item.disabled || item.noFocus ? undefined : 0"
              :disabled="item.disabled"
              class="focus-ring-none data-[focused]:bg-md"
              :class="[item.disabled && 'cursor-default']"
              :data-focused="
                !isMobile &&
                !item.noFocus &&
                !item.disabled &&
                (indexOfFocusedItem === item.index || openSubdropdownIndex === item.index)
                  ? ''
                  : undefined
              "
              @keydown.enter.stop="onKeydownItem"
              @mouseover="() => !item.disabled && focusItem(item.index)"
              @focusin="() => !item.disabled && focusItem(item.index)">
              <DropdownMenuItem :item="item" @click="close" />
            </DynamicScrollerItem>
          </template>
          <template #after>
            <div v-if="slots.extra" @mouseenter="clearFocus" @focusin="clearFocus">
              <slot name="extra" :close="close" />
            </div>
            <LoadingSpinner v-if="loading" class="py-1.5" small :immediate="processedSections.length === 0" />
          </template>
        </DynamicScroller>
        <slot name="footer" :close="close" />
        <Teleport v-if="isOpen" to="body">
          <div class="absolute inset-0" @click="close" @keydown.escape="close" />
        </Teleport>
      </Menu>
    </template>
  </GlobalPopper>

  <template v-else-if="!dropdownEmpty && showMobileSheet">
    <div
      v-bind="$attrs"
      class="flex"
      :class="[
        $attrs.class,
        {
          'max-w-full grow': block,
          'h-full': heightBlock,
          'cursor-pointer': !disabled,
        },
      ]"
      @click="open"
      @keydown="open">
      <slot />
    </div>

    <MobileBottomSheet
      ref="mobileSheet"
      :entity="isOpen"
      :include-back="mobileIncludeBack"
      :hidden="isHidden"
      :title="headerTitle"
      :include-done="mobileIncludeDone"
      :include-search="showSearch"
      :should-update-keyboard-height="showSearch"
      @close="close">
      <div class="flex-1 divide-y divide-md overflow-y-auto">
        <div
          v-for="(section, sectionIndex) in filteredSections"
          :key="`section-${sectionIndex}`"
          :class="['flex flex-col', showSearch && 'max-h-80']">
          <span v-if="section.showTitle" class="mt-3 pr-3 pl-5 text-xs font-semibold text-vlt uppercase select-none">
            {{ section.title }}
          </span>
          <template v-for="(item, itemIndex) in section.items" :key="`section-${sectionIndex}-${itemIndex}`">
            <DropdownMenuItem
              v-if="
                !enableShowMore ||
                hasSingleFilteredSection ||
                itemIndex < MAX_ITEMS_PER_SECTION ||
                expandedSections.has(section.title)
              "
              :item="item"
              @click="() => onMenuItemClick(item)" />
          </template>
          <button
            v-if="
              enableShowMore &&
              !hasSingleFilteredSection &&
              section.showTitle &&
              section.items.length > MAX_ITEMS_PER_SECTION + 1 &&
              !expandedSections.has(section.title)
            "
            type="button"
            class="mt-1 flex w-full items-center justify-center rounded-sm py-1.5 text-xs font-medium text-vlt"
            @click.prevent.stop="toggleSection(section.title)"
            @mousedown.prevent>
            Show more
          </button>
        </div>
        <div
          v-if="filteredSections.length === 0"
          tabindex="0"
          class="flex cursor-default items-center gap-2 py-3.5 pr-2 pl-5 text-vlt opacity-50 select-none">
          <AlertIcon class="icon-md text-vlt" />
          <span>No results, search again</span>
        </div>
        <slot name="extra" :close="close" />
      </div>
      <slot name="footer" :close="close" />
    </MobileBottomSheet>
  </template>
</template>

<style>
@reference "#frontend.css";
.v-popper--theme-dropdown .v-popper__arrow-container {
  display: none;
}
.v-popper--theme-dropdown .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-dropdown .v-popper__inner {
  border-width: 0px;
  border-radius: 4px;
  background-color: transparent;
}
</style>
